import {
  DeleteOutlined,
  DownOutlined,
  FormOutlined,
  LoadingOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button, Divider, Form, message, Select } from 'antd';
import SafeImage from 'components/SafeImage';
import { useMemo, useState } from 'react';
import ItemSelectModal from './ItemSelectModal';
import useModal from 'hooks/useModal';
import { useFormikContext } from 'formik';
import { FV } from './share';
import { useRequest } from 'ahooks';
import { getErpSkus } from '../api';
import ErpSku from './ErpSku';
import Empty from 'components/Empty';
import { FoldLine, Wrapper } from './ErpItemList';

interface ItemInfo {
  item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
  skuInfo: Pick<Conversation.ProductSKUInfo, 'sku' | 'skuId' | 'sellerSku'>[];
  skuId?: string;
}

function StoreItemList({
  storeId,
  itemOffsale,
  item,
  skuInfo,
  skuId,
}: {
  storeId: number;
  itemOffsale?: boolean;
} & ItemInfo) {
  const { t } = useTranslate();

  const [_itemList, setItemList] = useState<ItemInfo[]>([{ item, skuInfo, skuId }]);

  const itemList = useMemo(() => {
    return _itemList.map(({ item, skuInfo, ...others }, index) => {
      const usedSku = _itemList.slice(0, index).map(({ skuId }) => skuId);
      return {
        item,
        skuInfo: skuInfo.filter((_) => !usedSku.includes(_.skuId)),
        ...others,
      };
    });
  }, [_itemList]);

  const [fold, setFold] = useState(true);

  const offsaleItemId = itemOffsale ? item.externalItemId : undefined;

  const { values, setValues } = useFormikContext<FV>();

  return (
    <Wrapper>
      <section>
        <StoreItem
          key={itemList[0].item.externalItemId}
          index={0}
          offsaleItemId={offsaleItemId}
          storeId={storeId}
          itemInfo={itemList[0]}
          onItemUpdate={(item) => {
            setItemList([item, ...itemList.slice(1)]);
          }}
        />
        {itemList.length > 2 && (
          <FoldLine>
            <span />
            <Button type="link" onClick={() => setFold(!fold)}>
              {fold ? `${t('展开补发商品')}（${itemList.length - 2}）` : t('phrase.收起')}
              {fold ? <DownOutlined /> : <UpOutlined />}
            </Button>
            <span />
          </FoldLine>
        )}
        {itemList.length > 1 &&
          itemList.slice(1).map((itemInfo, i) => {
            const index = i + 1;
            return (
              <ItemWrapper>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setItemList((pre) => [...pre.slice(0, index), ...pre.slice(index + 1)]);
                    setValues({
                      ...values,
                      skus: [
                        ...(values.skus || []).slice(0, index),
                        ...(values.skus || []).slice(index + 1),
                      ],
                    });
                  }}
                />
                <StoreItem
                  key={itemInfo.item.externalItemId}
                  index={index}
                  hide={fold && index < itemList.length - 1}
                  offsaleItemId={offsaleItemId}
                  storeId={storeId}
                  itemInfo={itemInfo}
                  onItemUpdate={(item) => {
                    setItemList([...itemList.slice(0, index), item, ...itemList.slice(index + 1)]);
                  }}
                />
              </ItemWrapper>
            );
          })}
      </section>
      {itemList.length < 5 && (
        <Button
          ghost
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            const { item, skuInfo } = _itemList[_itemList.length - 1];
            setItemList([..._itemList, { item, skuInfo }]);
          }}
        >
          {t('新增补发商品')}
        </Button>
      )}
    </Wrapper>
  );
}

function StoreItem({
  index,
  hide: hideProp,
  offsaleItemId,
  storeId,
  itemInfo,
  onItemUpdate,
}: {
  index: number;
  hide?: boolean;
  offsaleItemId?: string;
  storeId: number;
  itemInfo: ItemInfo;
  onItemUpdate: (itemInfo: ItemInfo) => void;
}) {
  const { item, skuInfo, skuId } = itemInfo;

  const { t } = useTranslate();

  const { visible, show, hide } = useModal();

  const {
    values,
    values: { skus = [] },
    setValues,
  } = useFormikContext<FV>();

  const sku = skus[index];

  const setSku = (sku?: Todo.ErpOrderSku) =>
    setValues({
      ...values,
      skus: [...skus.slice(0, index), sku, ...skus.slice(index + 1)] as any,
    });

  const { data, loading } = useRequest(
    () => {
      const skuCode = skuInfo.find((_) => _.skuId === skuId)?.sellerSku;
      return getErpSkus({
        searchType: 'STORE_ITEM',
        storeId,
        skuId,
        skuCode,
        page: 1,
        pageSize: 1,
      } as any).then((res) => {
        if (res.length) {
          const { skuId: erpSkuId, skuName, imgUrl } = res[0];
          setSku({
            skuId: erpSkuId,
            skuName,
            imgUrl,
            skuAmount: '0',
            skuQuantity: 1,
            subSkus: [],
            relatedSpuId: item.externalItemId,
            relatedSkuId: skuId,
          });
        } else {
          setSku(undefined);
        }
        return res;
      });
    },
    {
      ready: Boolean(skuId && !sku),
      refreshDeps: [skuId, storeId],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  return (
    <Container style={hideProp ? { display: 'none' } : undefined}>
      <Form.Item required label={`${t('店铺')} SPU`}>
        <Item>
          <div>
            <SafeImage hasSmallPic="both" src={item.itemPic} width={48} height={48} />
            {offsaleItemId === item.externalItemId && (
              <span>
                <div>{t('knowledge.已失效')}</div>
              </span>
            )}
          </div>
          <section>{item.itemName}</section>
          <Divider type="vertical" />
          <Button type="text" icon={<FormOutlined />} onClick={show}>
            {t('btn_modify')}
          </Button>
          <ItemSelectModal
            visible={visible}
            storeId={storeId}
            item={item}
            onCancel={hide}
            onChange={(item, skuInfo) => {
              hide();
              setSku(undefined);
              onItemUpdate({
                item,
                skuInfo,
              });
            }}
          />
        </Item>
      </Form.Item>
      <Form.Item required label={`${t('店铺')} SKU | ${t('SKU货号')}`}>
        <Select
          dropdownMatchSelectWidth={false}
          showSearch
          optionFilterProp="label"
          placeholder={t('department.请选择x', { x: 'SKU' })}
          value={skuId}
          onChange={(value) => {
            setSku(undefined);
            onItemUpdate({
              item,
              skuInfo,
              skuId: value,
            });
          }}
          options={skuInfo.map(({ skuId, sku, sellerSku }) => ({
            value: skuId,
            label: `${sku} | ${sellerSku}`,
          }))}
        />
      </Form.Item>
      {data?.length && sku ? (
        <ErpSku index={index} erpItemSku={data[0]} />
      ) : (
        <section>
          {loading ? (
            <LoadingOutlined />
          ) : (
            <Form.Item label={`${t('ERP系统商品')}（${t('将使用此系统商品创建补发单')}）：`}>
              <Empty />
            </Form.Item>
          )}
        </section>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 18px 16px;
  margin-bottom: 16px;
  background: #f7f9fa;
  &:last-of-type {
    margin-bottom: 0;
  }
  > section {
    height: 138px;
    .ant-form-item {
      margin-bottom: 0;
    }
    .ant-empty {
      height: 108px;
      margin: 0;
      background: #fff;
      .ant-empty-image {
        height: 80px;
        margin-bottom: 0;
        > img {
          height: 80px;
        }
      }
    }
  }
`;

const ItemWrapper = styled.div`
  position: relative;
  > button {
    position: absolute;
    top: 14px;
    right: 12px;
    z-index: 1;
  }
`;

export const Item = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: solid 1px #d3d7e0;
  background: #fff;
  > div {
    flex: 0 0 auto;
    position: relative;
    margin-right: 12px;
    > span {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #0003;
      > div {
        font-size: 12px;
        padding: 0 4px;
        color: #fff;
        background: #000a;
        border-radius: 8px;
      }
    }
  }
  > section {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
`;

export default StoreItemList;
